
// React Imports
import * as React from "react";
import {useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";

// Mui Imports
import {
    DataGrid,
    GridToolbarColumnsButton,
    GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport,
    GridToolbarFilterButton
} from '@mui/x-data-grid';
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputLabel,
    ListItemIcon,
    ListItemText,
    Menu, Select,
    Stack,
    Tooltip
} from "@mui/material";

// App Imports
import {
    deleteUsage, getFileIds, processUsage, usageSearch
} from "./UsageClient";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {changeUsage} from "./usageSlice";
import {changeCrudSnackbar} from "../../CommonComponents/crudSnackbarSlice";
import UsageDrawer from "./UsageDrawer";
import CustomFooter from "../../CommonComponents/GridCustomFooter";
import CrudSnackbar from "../../CommonComponents/CrudSnackbar";
import {changeUploadFile} from "../../CommonComponents/UploadFile/uploadFileSlice";
import Button from "@mui/material/Button";
import UploadFileDrawer from "../../CommonComponents/UploadFile/UploadFileDrawer";
import SearchIcon from "@mui/icons-material/Search";
import UsageSearch from "./UsageSearch";
import FormControl from "@mui/material/FormControl";
import LinearIndeterminate from "../../CommonComponents/Progress";
import AddFiltersExportToolbar from "../../CommonComponents/AddFiltersExportToolbar";
import {ViewershipGridColumns} from "../Viewership/ViewershipGridColumns";
import {changeViewership} from "../Viewership/viewershipSlice";
import {UsageGridColumns} from "./UsageGridColumns";
import {changeExternalData} from "../externalDataSlice";
import NoSearchCriteriaSnackbar from "../../CommonComponents/NoSearchCriteriaSnackbar";
import UsageInfoDialogue from "./Info/UsageInfoDialogue";

// main function
export default function UsageGrid() {

    // ****************** BEGIN STATES ****************************

    const [usages, setUsages] = useState([]);
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [gridVisible, setGridVisible] = React.useState(true);
    const [showControls, setShowControls] = useState(true);
    const [showSearch, setShowSearch] = useState(false);
    const hideActions = localStorage.getItem('hideActions') === "true";
    const [usageInfoOpen, setUsageInfoOpen] = useState(false);

    // ****************** END STATES ****************************


    // *********************** BEGIN STORE ********************************

    const selectorUsageId = useSelector((state) => state.usageSlc.value.id);
    const selectorUsageMcode = useSelector((state) => state.usageSlc.value.mcode);
    const selectorUsageName = useSelector((state) => state.usageSlc.value.name);
    const selectorUsageDescription = useSelector((state) => state.usageSlc.value.description);
    const selectorUsageCategory = useSelector((state) => state.usageSlc.value.category);
    const selectorDrawerState = useSelector((state) => state.usageSlc.value.usageDrawerState);

    const selectorUploadFileDrawerState = useSelector((state) => state.uploadFileSlc.value.uploadFileDrawerState);

    const selectorFileId = useSelector((state) => state.usageSearchSlc.value.fileId);
    const selectorPropertyId = useSelector((state) => state.usageSearchSlc.value.propertyId);
    const selectorProductName = useSelector((state) => state.usageSearchSlc.value.productName);
    const selectorEpisodeName = useSelector((state) => state.usageSearchSlc.value.episodeName);
    const selectorPhotoStartDateFrom = useSelector((state) => state.usageSearchSlc.value.photoStartDateFrom);
    const selectorPhotoStartDateTo = useSelector((state) => state.usageSearchSlc.value.photoStartDateTo);
    const selectorSeriesExternalId = useSelector((state) => state.usageSearchSlc.value.seriesExternalId);
    const selectorSeriesName = useSelector((state) => state.usageSearchSlc.value.seriesName);
    const selectorSeasonExternalId = useSelector((state) => state.usageSearchSlc.value.seasonExternalId);
    const selectorSeasonNumberFrom = useSelector((state) => state.usageSearchSlc.value.seasonNumberFrom);
    const selectorSeasonNumberTo = useSelector((state) => state.usageSearchSlc.value.seasonNumberTo);
    const selectorSeasonName = useSelector((state) => state.usageSearchSlc.value.seasonName);

    const selectorPlatformId = useSelector((state) => state.usageSearchSlc.value.platformId);
    const selectorPlatformName = useSelector((state) => state.usageSearchSlc.value.platformName);
    const selectorService = useSelector((state) => state.usageSearchSlc.value.service);
    const selectorUsageMarket = useSelector((state) => state.usageSearchSlc.value.usageMarket);
    const selectorTerritory = useSelector((state) => state.usageSearchSlc.value.territory);
    const selectorLicenseId = useSelector((state) => state.usageSearchSlc.value.licenseId);
    const selectorLicensee = useSelector((state) => state.usageSearchSlc.value.licensee);
    const selectorUseStartFrom = useSelector((state) => state.usageSearchSlc.value.useStartFrom);
    const selectorUseStartTo = useSelector((state) => state.usageSearchSlc.value.useStartTo);
    const selectorUseEndFrom = useSelector((state) => state.usageSearchSlc.value.useEndFrom);
    const selectorUseEndTo = useSelector((state) => state.usageSearchSlc.value.useEndTo);
    const selectorLanguage = useSelector((state) => state.usageSearchSlc.value.language);
    const selectorId = useSelector((state) => state.usageSearchSlc.value.id);
    const selectorExternalId = useSelector((state) => state.usageSearchSlc.value.externalId);
    const selectorStakeholderId = useSelector((state) => state.usageSearchSlc.value.stakeholderId);
    const selectorStatus = useSelector((state) => state.usageSearchSlc.value.status);
    const selectorParticipations = useSelector((state) => state.usageSearchSlc.value.participations);
    const selectorResiduals = useSelector((state) => state.usageSearchSlc.value.residuals);
    const selectorRoyalties = useSelector((state) => state.usageSearchSlc.value.royalties);

    const selectedId = useSelector((state) => state.usageSlc.value.id);

    const [fileIdDropdown, setFileIdDropdown] = useState([]);
    const [selectedFileId, setSelectedFileId] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const dispatchData = useDispatch();

    const selectorUsageGridRefresh = useSelector((state) => state.usageSlc.value.usageGridRefresh);

    // ********************** END STORE ********************************


    // ****************** BEGIN FETCH ****************************


    //
    // const fetchUsages = () =>{
    //     if(
    //         selectorFileId !== null ||
    //         selectorPropertyId !== null ||
    //         selectorProductName !== null ||
    //         selectorEpisodeName !== null ||
    //         selectorPhotoStartDateFrom !== null ||
    //         selectorPhotoStartDateTo !== null ||
    //         selectorSeriesExternalId !== null ||
    //         selectorSeriesName !== null ||
    //         selectorSeasonExternalId !== null ||
    //         selectorSeasonNumberFrom !== null ||
    //         selectorSeasonNumberTo !== null ||
    //         selectorSeasonName !== null ||
    //         selectorPlatformId !== null ||
    //             selectorPlatformName !== null ||
    //             selectorService !== null ||
    //             selectorUsageMarket !== null ||
    //             selectorTerritory !== null ||
    //             selectorLicenseId !== null ||
    //             selectorLicensee !== null ||
    //             selectorUseStartFrom !== null ||
    //             selectorUseStartTo !== null ||
    //             selectorUseEndFrom !== null ||
    //             selectorUseEndTo !== null ||
    //             selectorLanguage !== null ||
    //             selectorId !== null ||
    //             selectorExternalId !== null ||
    //         selectorStakeholderId !== null ||
    //             selectorStatus !== null ||
    //         selectorParticipations !== null ||
    //         selectorResiduals !== null ||
    //         selectorRoyalties !== null
    //     )
    //     {
    //         {
    //             usageSearch(
    //                 selectorFileId,
    //                 selectorPropertyId,
    //                 selectorProductName,
    //                 selectorEpisodeName,
    //                 selectorPhotoStartDateFrom,
    //                 selectorPhotoStartDateTo,
    //                 selectorSeriesExternalId,
    //                 selectorSeriesName,
    //                 selectorSeasonExternalId,
    //                 selectorSeasonNumberFrom,
    //                 selectorSeasonNumberTo,
    //                 selectorSeasonName,
    //                 selectorPlatformId,
    //                 selectorPlatformName,
    //                 selectorService,
    //                 selectorUsageMarket,
    //                 selectorTerritory,
    //                 selectorLicenseId,
    //                 selectorLicensee,
    //                 selectorUseStartFrom,
    //                 selectorUseStartTo,
    //                 selectorUseEndFrom,
    //                 selectorUseEndTo,
    //                 selectorLanguage,
    //                 selectorId,
    //                 selectorExternalId,
    //                 selectorStakeholderId,
    //                 selectorStatus,
    //                 selectorParticipations,
    //                 selectorResiduals,
    //                 selectorRoyalties
    //             )

    //                 .then(data => {
    //                         console.log(data);
    //                         setUsages(data);
    //                     }
    //                 )
    //         }
    //     }
    //     else{console.log("search triggered, nothing to search.")}}

    const fetchUsage = () => {

        const request = {

            fileId: selectorFileId,

            propertyId: selectorPropertyId,

            productName: selectorProductName,

            episodeName: selectorEpisodeName,

            photoStartDateFrom: selectorPhotoStartDateFrom,
            photoStartDateTo: selectorPhotoStartDateTo,

            seriesExternalId: selectorSeriesExternalId,

            seriesName: selectorSeriesName,

            seasonExternalId: selectorSeasonExternalId,

            seasonNumberFrom: selectorSeasonNumberFrom,
            seasonNumberTo: selectorSeasonNumberTo,

            seasonName: selectorSeasonName,

            platformId: selectorPlatformId,

            platformName: selectorPlatformName,

            service: selectorService,

            usageMarket: selectorUsageMarket,

            territory: selectorTerritory,

            licenseId: selectorLicenseId,

            licensee: selectorLicensee,

            useStartFrom: selectorUseStartFrom,
            useStartTo: selectorUseStartTo,

            useEndFrom: selectorUseEndFrom,
            useEndTo: selectorUseEndTo,

            language: selectorLanguage,

            id: selectorId,

            externalId: selectorExternalId,

            stakeholderId: selectorStakeholderId,

            status: selectorStatus,

            participations: selectorParticipations,

            residuals: selectorResiduals,

            royalties: selectorRoyalties
        };

        console.log(
            JSON.stringify(request, null, 2)
        );

        const hasSearchCriteria = Object.values(request).some(
            value =>
                value !== null &&
                value !== undefined &&
                value !== ''
        );

        if (hasSearchCriteria) {

            dispatchData(changeExternalData({ submitting: true }));

            usageSearch(request)



                .then(data => {

                    console.log(data);

                    setUsages(data);

                    if (!data || data.length === 0) {
                        console.log("no matching records found");
                        dispatchData(changeExternalData({ snackbarOpen: true, snackbarSeverity: "info", snackbarMessage: "No matching records found" }));
                    }


                })

                .catch(err => {

                    console.error(err);

                })


                .finally(() => {

                    // setSubmitting(false);
                    dispatchData(changeExternalData({ submitting: false }));
                });


        } else {

            console.log(
                "search triggered, nothing to search."
            );
            dispatchData(changeExternalData({ snackbarOpen: true, snackbarSeverity: "warning", snackbarMessage: "No search criteria provided" }));
        }
    };

    // ****************** END FETCH ****************************


    function handleProcessFile(){
        setSubmitting(true);
        console.log("This is the file I will submit");
        console.log(selectedFileId);
        processUsage(selectedFileId)
            .then(()=>{setSubmitting(false)})

    };

    const handleFileIdChange = (fileIdEvent) => {
        setSelectedFileId(fileIdEvent.target.value);
        console.log("This is for the selected Value");
        console.log(fileIdEvent.target.value);
    };

    const fetchFileIds = () => {

        getFileIds()

            .then(data => {
                console.log("Guilds");
                console.log(data);
                console.log("Guilds with empty string");
                data.unshift({singleField: ''});
                console.log(data);
                setFileIdDropdown(data);

            })
    }

    useEffect(() => {
        console.log("This is for the File Id dropdown...");
        fetchFileIds();
    }, []);

    const handleUploadFileDrawer = () => {
        console.log("upload file drawer state changed");
        dispatchData(changeUploadFile({uploadFileDrawerState: true,
            uploadFileDrawerMode: "Usage"}));
    };

    //
    // useEffect(() => {
    //     fetchUsages();
    //     dispatchData(changeUsage({usageGridRefresh: false}));
    // }, [useSelector((state) => state.usageSlc.value.usageGridRefresh)===true]);
    //


    useEffect(() => {

        if (selectorUsageGridRefresh) {

            fetchUsage();

            dispatchData(changeUsage({
                usageGridRefresh: false
            }));
        }

    }, [selectorUsageGridRefresh]);

    // ****************** BEGIN CRUD ***********************************

    const handleAdd = () => {
        dispatchData(changeUsage({id: null, fileId: null,externalId: null, licenseId: null, platformId: null,
            propertyId: null, status: null, useStart: null, useEnd: null, language: null, stakeholderId: null,
            createdOn: null,createdBy:null,lastModifiedOn:null,lastModifiedBy:null,
            propertyName: null, initialMarket: null, genre: null, productionType: null,
            licenseAmount: null, licenseDisplayAmount: null, availBegin: null,
            availEnd: null, executionDate: null, licensee: null,
            platformInternalId: null, platformExternalId: null, platformCode: null, platformName: null,
            service: null, platformTerritory: null, usageMarket: null, owner: null,
            usageDrawerState: true, usageDrawerMode: "Add"}));
    };


    const removeUsage = () => {
        deleteUsage(selectorUsageId).then(() => {
            setDeleteOpen(false);
            dispatchData(changeUsage({usageGridRefresh: true}));
            dispatchData(changeCrudSnackbar({crudOpen: true, crudSeverity: "success", crudMessage: "Record deleted"}));
        })
    }

    const handleDeleteOpen = () => {
        setDeleteOpen(true);
    };

    function DeleteConfirmationDialog() {
        // const [open, setOpen] = useState(false);

        const handleClickOpen = () => {
            setDeleteOpen(true);
        };

        const handleClose = () => {
            setDeleteOpen(false);
        };


        const handleDelete = () => {

            deleteUsage(selectedId).then(() => {
                setDeleteOpen(false);
                dispatchData(changeUsage({usageGridRefresh: true}));
                dispatchData(changeCrudSnackbar({crudOpen: true, crudSeverity: "success", crudMessage: "Record deleted"}));
            })
            console.log('Item deleted');
        };

        return (
            <Dialog
                open={deleteOpen}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                sx={{
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'transparent', // makes backdrop transparent
                        boxShadow: '0 0 10px 5px rgba(0, 0, 0, 0.5)' // custom shadow box effect
                    }
                }}
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this item? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleDelete} autoFocus color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
    const handleDeleteCancel = () => {
        setDeleteOpen(false);
    };
    function handleSearch(){
        setShowSearch(!showSearch)
    }


    const gridClear = () => {

        setUsages([]);
    }

    // ****************** END CRUD ***********************************



    // ****************** BEGIN DRILLDOWN ****************************


    // ****************** END DRILLDOWN ****************************

    const handleClickedRow = (usages) => {

        console.log('Usage data from clicked row');
        console.log(usages);
        dispatchData(changeUsage({id: usages.id,  fileId: usages.fileId, externalId: usages.externalId,
            licenseId: usages.licenseId, platformId: usages.platformId,
            propertyId: usages.propertyId, status: usages.status, stakeholderId: usages.stakeholderId,
            useStart: usages.useStart, useEnd: usages.useEnd, language: usages.language,
            createdOn: usages.createdOn,createdBy:usages.createdBy,
            lastModifiedOn:usages.lastModifiedOn,lastModifiedBy:usages.lastModifiedBy,
            propertyName: usages.propertyName, initialMarket: usages.initialMarket, 
            genre: usages.genre, productionType: usages.productionType,
            licenseAmount: usages.licenseAmount, licenseDisplayAmount: usages.licenseDisplayAmount, 
            availBegin: usages.availBegin, availEnd: usages.availEnd, 
            executionDate: usages.executionDate, licensee: usages.licensee,
            platformInternalId: usages.platformInternalId, platformExternalId: usages.platformExternalId, 
            platformCode: usages.platformCode, platformName: usages.platformName,
            service: usages.service, platformTerritory: usages.platformTerritory, 
            usageMarket: usages.usageMarket, owner: usages.owner}));
    };



    const handleUsageInfo = () => {
        setUsageInfoOpen(true);
    };

    const handleCloseUsageInfo = () => {
        setUsageInfoOpen(false);
    };



    // ****************** BEGIN RETURN ****************************

    return (
        <div style={{ height:800 , width: '100%' }}>
            <Box sx={{height: 80, pt: 1, pl: 3}}>

                <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }} spacing={0}>

                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                        <Stack direction={"horizontal"}>
                            <Tooltip enterDelay={1000} enterNextDelay={1000} title={"Click for information."}>
                            <h3
                                style={{cursor:'pointer'}}   onClick={() => handleUsageInfo()}
                            >Usage</h3>
                            </Tooltip>
                                <UsageInfoDialogue
                                    open={usageInfoOpen}
                                    onClose={handleCloseUsageInfo}
                                />
                            {/*<SearchIcon*/}
                            {/*    sx={{pt: 2, ml: 2}}*/}
                            {/*    color={'primary'}*/}
                            {/*    onClick={handleSearch}*/}
                            {/*/>*/}
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                    color="primary"
                                    size="large"               // large icon & hit area
                                    onClick={handleSearch}
                                    sx={{ ml: 2 }}             // any spacing you want
                                >
                                    <SearchIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                            <Button sx={{pt: 1, ml: 3}}
                                    onClick={handleUploadFileDrawer}
                            >Upload</Button>
                        </Stack>
                    </Box>

                    <Box sx={{ flex: 0, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                        {/*<Box sx={{width:600}}/>*/}
                        {/*                    <Box sx={{pl: 10}}>*/}
                        <Stack direction={"horizontal"}>
                            <FormControl variant="standard" sx={{pr:1, minWidth: 100 }} >
                                <InputLabel sx={{ ml: 1}} htmlFor="fileId">File ID</InputLabel>
                                <Select
                                    labelId="demo-simple-select-filled-fileId"
                                    id="fileId"
                                    FreeSolo={true}
                                    label="fileId"
                                    onChange={handleFileIdChange}
                                    sx={{ width: 300}}
                                >
                                    {fileIdDropdown.map((fileIdItem) =>
                                        <MenuItem  FreeSolo={true} value={fileIdItem.singleField}>{fileIdItem.singleField}</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                            <Button sx={{pt: 2, ml: 1}}
                                    onClick={handleProcessFile}
                            >Process</Button>
                        </Stack>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                </Stack>

            </Box>
            {showSearch && <UsageSearch
                gridClear = {gridClear}
            />}
                {selectorUploadFileDrawerState && <UploadFileDrawer/>}
            {submitting && <LinearIndeterminate/>}
            {selectorDrawerState && <UsageDrawer
            />}
            {gridVisible && <DataGrid
                experimentalFeatures={{ newEditingApi: true }}
                initialState={{
                }}
                aria-label={"Usages"}
                rows={usages}
                disableDensitySelector={true}
                disableColumnSelector={true}
                columns={UsageGridColumns({
                    hideActions,
                    onEdit: () => dispatchData(changeUsage({ usageDrawerState: true, usageDrawerMode: "Edit" })),
                    onClone: () => dispatchData(changeUsage({ usageDrawerState: true, usageDrawerMode: "Clone" })),
                    onDelete: removeUsage
                })}
                onRowClick = {(usages)=>handleClickedRow(usages.row)}
                density={"compact"}
                pageSize={20}
                rowsPerPageOptions={[20]}
                rowKey={usage => usage.id}
                components={{
                    Toolbar: AddFiltersExportToolbar,
                    Footer: CustomFooter,
                }}
                componentsProps={{
                    toolbar: {
                        handleAdd: handleAdd
                    }
                }}
            />}
            <CrudSnackbar/>
            <NoSearchCriteriaSnackbar/>
        </div>

    );

    // ****************** END RETURN ****************************

}

